import styled from 'styled-components';
import { Palette } from 'config';

export const Container = styled.div`
  --preview-transition: 0.4s;
  --preview-background: ${({ darkMode }) => (darkMode ? '#252525' : Palette.PrimaryGray._000)};
  --preview-skeleton-bg: ${({ darkMode }) => (darkMode ? '#292929' : Palette.PrimaryGray._200)};
  --preview-skeleton-footer-bg: ${({ darkMode }) => (darkMode ? '#5f5d5d' : '#d5e0ec')};
  --preview-skeleton-item-bg: ${({ darkMode }) =>
    darkMode ? '#424242' : Palette.PrimaryGray._100};
  --preview-skeleton-action-bg: ${({ darkMode }) => (darkMode ? '#303030' : '#ddeaf8')}; // #bfccdc
  --preview-selected-dot-bg: ${({ darkMode }) => (darkMode ? '#292929' : Palette.PrimaryGray._400)};
  --preview-check-color: ${({ darkMode }) => (darkMode ? '#424141' : Palette.White._000)};
  --preview-landscape-start: #4f46e5;
  --preview-landscape-end: ${Palette.Cyan._500};
  --preview-landscape-text: ${Palette.White._000};
  --preview-creative-bg: ${({ darkMode }) => (darkMode ? '#151515' : '#e3e4e8')};
  --preview-creative-text: ${({ darkMode }) => (darkMode ? Palette.White._000 : '#010102')};
  --preview-creative-muted-text: ${({ darkMode }) => (darkMode ? '#b7b7bb' : '#636368')};
  --preview-creative-divider: ${({ darkMode }) =>
    darkMode ? 'rgba(183, 183, 187, 0.8)' : 'rgba(99, 99, 104, 0.45)'};
  --preview-icon-bg: ${({ darkMode }) => (darkMode ? '#424242' : Palette.White._000)};
  --preview-icon-border: ${({ darkMode }) => (darkMode ? 'var(--preview-skeleton-bg)' : '#ebeff4')};
  --preview-download-bg: ${({ darkMode }) => (darkMode ? '#292929' : Palette.White._000)};
  --preview-download-blue: #0381fe;
  --preview-info-color: #8e8e8e;
  --preview-info-bg: rgba(255, 255, 255, 0.2);
  --preview-button: ${({ darkMode }) => (darkMode ? Palette.Primary._500 : '#1689f9')};

  --preview-scale: 0.7;

  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  padding: 58px 24px 24px;
  transform: scale(var(--preview-scale));
  transform-origin: top center;
`;

export const PhoneFrame = styled.div`
  position: relative;
  width: 362px;
  height: 724px;
  padding: 12px;
  border: 3px solid #253244;
  border-radius: 42px;
  background: linear-gradient(
    135deg,
    ${Palette.CoolGray._900} 0%,
    #020617 45%,
    ${Palette.PrimaryGray._700} 100%
  );
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.22), inset 0 0 0 2px rgba(255, 255, 255, 0.18),
    inset 0 0 0 6px rgba(15, 23, 42, 0.9);
`;

export const SideButton = styled.div`
  position: absolute;
  left: -6px;
  top: ${({ middle }) => (middle ? '164px' : '96px')};
  width: 4px;
  height: ${({ middle }) => (middle ? '42px' : '52px')};
  border-radius: 4px 0 0 4px;
  background: ${Palette.PrimaryGray._700};
`;

export const PhoneScreen = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
  border-radius: 31px;
  background: var(--preview-background);
  color: ${Palette.PrimaryGray._700};
  font-family: Roboto, Arial, sans-serif;
  transition: background var(--preview-transition);
`;

export const StatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 0 16px;
  background: var(--preview-background);
  font-size: 11px;
  line-height: 16px;
  transition: background var(--preview-transition);
`;

export const StatusTime = styled.span`
  color: ${({ darkMode }) => (darkMode ? Palette.White._000 : Palette.PrimaryGray._600)};
  font-weight: 500;
  transition: color var(--preview-transition);
`;

export const StatusIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${({ darkMode }) => (darkMode ? Palette.White._000 : Palette.PrimaryGray._600)};
  transition: color var(--preview-transition);
`;

export const SignalIcon = styled.div`
  display: flex;
  align-items: end;
  gap: 1px;
  height: 8px;

  span {
    display: block;
    width: 2px;
    border-radius: 2px;
    background: ${({ darkMode }) => (darkMode ? Palette.White._000 : Palette.PrimaryGray._500)};
    transition: background var(--preview-transition);
  }

  span:nth-child(1) {
    height: 3px;
  }

  span:nth-child(2) {
    height: 5px;
  }

  span:nth-child(3) {
    height: 7px;
  }
`;

export const BatteryIcon = styled.div`
  position: relative;
  width: 11px;
  height: 6px;
  border: 1px solid ${({ darkMode }) => (darkMode ? Palette.White._000 : Palette.PrimaryGray._500)};
  border-radius: 1px;
  transition: border-color var(--preview-transition);

  &::before {
    content: '';
    position: absolute;
    left: 1px;
    top: 1px;
    width: 4px;
    height: 2px;
    background: ${({ darkMode }) => (darkMode ? Palette.White._000 : Palette.PrimaryGray._500)};
    transition: background var(--preview-transition);
  }

  &::after {
    content: '';
    position: absolute;
    right: -3px;
    top: 1px;
    width: 1px;
    height: 2px;
    background: ${({ darkMode }) => (darkMode ? Palette.White._000 : Palette.PrimaryGray._500)};
    transition: background var(--preview-transition);
  }
`;

export const BatteryText = styled.span`
  font-size: 11px;
`;

export const PreviewHeader = styled.div`
  padding: 0 16px 12px;
`;

export const HeaderTitle = styled.div`
  color: ${({ darkMode }) => (darkMode ? Palette.White._000 : '#2f2f2f')};
  font-size: 21px;
  font-weight: 700;
  line-height: 32px;
  transition: color var(--preview-transition);
`;

export const HeaderMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: ${({ darkMode }) => (darkMode ? Palette.CoolGray._300 : Palette.PrimaryGray._500)};
  font-size: 11px;
  line-height: 16px;
  transition: color var(--preview-transition);
`;

export const SkeletonCard = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 12px;
  height: ${({ large }) => (large ? '176px' : '110px')};
  margin: 0 16px 10px;
  overflow: hidden;
  border-radius: 2px;
  background: var(--preview-skeleton-item-bg);
  box-shadow: rgba(0, 0, 0, 0.04) 0px 2px 4px 0px inset;
  transition: background var(--preview-transition);
`;

export const SkeletonFooter = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 80px;
  padding: 10px 14px;
  background: var(--preview-skeleton-footer-bg);
  transition: background var(--preview-transition);
`;

export const SkeletonIcon = styled.div`
  position: relative;
  width: 44px;
  height: 44px;
  flex: 0 1 44px;
  border-radius: 12px;
  background: var(--preview-skeleton-item-bg);
  transition: background var(--preview-transition);
`;

export const SkeletonLines = styled.div`
  position: relative;
  z-index: 1;
  flex: 1 1;
`;

export const SkeletonLine = styled.div`
  width: ${({ width }) => width || '100%'};
  height: 10px;
  margin-bottom: 8px;
  border-radius: 3px;
  background: var(--preview-skeleton-item-bg);
  transition: background var(--preview-transition);

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SkeletonAction = styled.div`
  width: 38px;
  height: 38px;
  flex: 0 1 38px;
  border-radius: 50%;
  background: var(--preview-skeleton-action-bg);
  transition: background var(--preview-transition);
`;

export const CreativeCard = styled.div`
  position: relative;
  margin: 0 16px 10px;
  overflow: hidden;
  border-radius: 2px;
`;

export const SelectedDot = styled.div`
  position: absolute;
  top: 9px;
  left: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--preview-selected-dot-bg);
  transition: background var(--preview-transition);

  &::after {
    content: '';
    width: 8px;
    height: 4px;
    border-left: 2px solid var(--preview-check-color);
    border-bottom: 2px solid var(--preview-check-color);
    transform: rotate(-45deg);
    transition: border-color var(--preview-transition);
  }
`;

export const LandscapeWrap = styled.div`
  height: 157px;
  background: linear-gradient(
    135deg,
    var(--preview-landscape-start) 0%,
    var(--preview-landscape-end) 100%
  );
`;

export const LandscapeImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
`;

export const LandscapePlaceholder = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: var(--preview-landscape-text);
  text-align: center;
  text-transform: uppercase;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }

  &::before {
    right: -14px;
    top: -40px;
    width: 112px;
    height: 112px;
  }

  &::after {
    left: -42px;
    bottom: -54px;
    width: 132px;
    height: 132px;
    background: rgba(0, 0, 0, 0.1);
  }

  strong {
    position: relative;
    z-index: 1;
    font-size: 22px;
    line-height: 28px;
    letter-spacing: 0;
  }

  span {
    position: relative;
    z-index: 1;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    opacity: 0.85;
    text-transform: none;
  }
`;

export const CreativeDetails = styled.div`
  display: flex;
  align-items: center;
  min-height: 96px;
  padding: 12px 11px 13px 14px;
  color: var(--preview-creative-text);
  background: var(--preview-creative-bg);
  transition: all var(--preview-transition);
`;

export const IconWrap = styled.div`
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid var(--preview-icon-border);
  background: var(--preview-icon-bg);
  transition: border var(--preview-transition), background var(--preview-transition);
`;

export const IconImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const CreativeBody = styled.div`
  min-width: 0;
  flex: 1;
  margin: 0 10px;
`;

export const CreativeTitle = styled.div`
  overflow: hidden;
  display: -webkit-box;
  color: var(--preview-creative-text);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  min-height: 20px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  word-break: break-word;
  transition: color var(--preview-transition);
`;

export const CreativeStats = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  margin-top: 8px;
`;

export const StatBlock = styled.div`
  min-width: 0;
  flex-shrink: 0;
`;

export const StatHead = styled.div`
  color: var(--preview-creative-text);
  font-size: 11px;
  font-weight: 700;
  line-height: 13px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color var(--preview-transition);
`;

export const StarIcon = styled.svg`
  width: 10px;
  height: 10px;
  flex: 0 0 10px;
  margin-left: 1px;
  margin-bottom: -1px;
  fill: var(--preview-creative-text);
  transition: fill var(--preview-transition);
`;

export const StatLabel = styled.div`
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--preview-creative-muted-text);
  font-size: 9px;
  font-weight: 400;
  line-height: 12px;
  text-align: center;
  transition: color var(--preview-transition);
`;

export const StatDivider = styled.div`
  width: 1px;
  height: 15px;
  flex: 0 0 1px;
  margin: 0 6px;
  background: var(--preview-creative-divider);
  transition: background var(--preview-transition);
`;

export const DownloadButton = styled.button`
  position: relative;
  width: 38px;
  height: 38px;
  margin-top: auto;
  margin-bottom: 7px;
  border: 0;
  border-radius: 50%;
  background: var(--preview-download-bg);
  transition: background var(--preview-transition);

  & svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
  }

  & svg > * {
    fill: var(--preview-download-blue);
  }
`;

export const ContinueButton = styled.button`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 16px;
  height: 52px;
  background-color: var(--preview-background);
  border: 0;
  z-index: 10;
  transition: background-color var(--preview-transition);

  &::before {
    content: 'Continue';
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 0;
    height: 37px;
    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 18px;
    background-color: var(--preview-button);
    color: ${Palette.White._000};
    font-size: 14px;
    font-weight: 500;
    transition: background-color var(--preview-transition);
  }
`;

export const ThemeToggle = styled.div`
  margin-top: 40px;
  transform: scale(calc(1 / var(--preview-scale)));

  label:last-child {
    min-width: auto;
    color: ${Palette.PrimaryGray._700};
    font-size: 14px;
    font-weight: 500;
  }
`;
